import React, { useEffect, useLayoutEffect } from "react";
import { useSearchParams, Link, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import SalyLeft from "../../assets/SalyLeft.png";
import { useSignIn } from "../../hooks";
import errorimg from "../../assets/error.svg";
import useWindowWidth from "../../hooks/useWindowWidth";
import SuccessImage from "../../assets/success.png";
import { ThankYouSignUp } from "../../modals";

import store from "../../redux/store";
import { authIsAuthenticated } from "../../redux/selectors/authSelectors";
import { signOut } from "../../redux/actions/authActions";
import { clearAppSession, SIGNED_OUT_QUERY_PARAM } from "../../common/sessionClear";
import { isAuthRevoked, markAuthRevoked } from "../../common/authSession";
import {
  applyPostSignInRedirect,
  resolvePostSignInRedirect,
  syncSignInRedirectSessionStorage,
  clearSignInRedirectSessionStorage,
} from "../../common/signInRedirect";

import "./SignIn.scss";

const CLASSLINK_ERROR_MESSAGES = {
  student_only: "Career Scoops via ClassLink is for students only.",
  inactive: "Your account is inactive. Contact your school administrator.",
  missing_email: "Your ClassLink account does not include an email address.",
  not_configured: "ClassLink sign-in is not configured on the server yet.",
  invalid_state: "Your ClassLink session expired. Open Career Scoops again from LaunchPad.",
  token_exchange_failed: "ClassLink sign-in failed. Try again from LaunchPad.",
  missing_token:
    "ClassLink did not return a login token. LaunchPad must start at Career Scoops login (ask your admin to fix the tile link).",
  invalid_handoff: "Could not complete ClassLink sign-in. Try again from LaunchPad.",
  unknown: "Something went wrong during ClassLink sign-in.",
};

function classLinkLoginStartUrl() {
  const base = (process.env.REACT_APP_API_URL || "").replace(/\/?$/, "/");
  if (!base) return null;
  return `${base}classlink/login/`;
}

const SignIn = () => {
  const dispatch = useDispatch();
  const {
    email,
    setEmail,
    password,
    setPassword,
    handleLogin,
    isLoading,
    showEmailError,
    emailError,
    passwordError,
    showPasswordError,
    isErroModalOpened,
    closeErrorModal,
    getErrorFromBackend,
  } = useSignIn();

  const { isMobile } = useWindowWidth();

  const [searchParams] = useSearchParams();
  const location = useLocation();
  const redirect = searchParams.get("redirect");
  const signedOut = searchParams.get(SIGNED_OUT_QUERY_PARAM);
  const classlinkError = searchParams.get("classlink_error");
  const classlinkRetryUrl = classLinkLoginStartUrl();

  useLayoutEffect(() => {
    if (signedOut) {
      markAuthRevoked();
      clearAppSession();
      clearSignInRedirectSessionStorage();
      dispatch(signOut());
      window.history.replaceState(null, "", "/signin");
      return;
    }

    if (isAuthRevoked()) {
      // Stay on the sign-in form; successful login clears revoked in the saga.
      return;
    }

    syncSignInRedirectSessionStorage();
    const wantsRedirect = resolvePostSignInRedirect(redirect);
    if (!wantsRedirect) return;
    if (!authIsAuthenticated(store.getState())) return;
    applyPostSignInRedirect({ redirectHint: redirect });
  }, [location.search, redirect, signedOut, dispatch]);

useEffect(() => {
    if (
      !localStorage.isFirstSeen &&
      !localStorage.CAREER_SCOOPS_AUTH_ACCESS?.length
    ) {
      localStorage.isFirstSeen = true;
    }
  }, []);

  return (
    <section className="SignIn">
      {!isLoading ? (
        <div className="SignIn-cont">
          <p className="SignIn-cont__title">Log in</p>
          {classlinkError ? (
            <div className="Signin-errorCont mb-4" role="alert">
              <img src={errorimg} alt="" />
              <p className="Signin-errorCont__text">
                {CLASSLINK_ERROR_MESSAGES[classlinkError] ||
                  CLASSLINK_ERROR_MESSAGES.unknown}
              </p>
              {classlinkRetryUrl ? (
                <p className="Signin-errorCont__text mt-2">
                  <a href={classlinkRetryUrl}>Try ClassLink sign-in again</a>
                </p>
              ) : null}
            </div>
          ) : null}
          <div className="SignIn-cont__input">
            <div className="input">
              <p>Email</p>
              <input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                type="email"
              />
              {showEmailError ? (
                <div className="Signin-errorCont">
                  <img src={errorimg} alt="errorimg" />
                  <p className="Signin-errorCont__text">{emailError}</p>
                </div>
              ) : null}
            </div>
            <div className="input">
              <p>Password</p>
              <input
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                type="password"
              />
              {showPasswordError ? (
                <div className="Signin-errorCont">
                  <img src={errorimg} alt="errorimg" />
                  <p className="Signin-errorCont__text">{passwordError}</p>
                </div>
              ) : null}
            </div>
          </div>
          <div className="SignIn-cont__link">
            <Link to={"/forgotpassword"}>Forgot Password</Link>
          </div>

          <button
            onClick={() => {
              handleLogin(email, password);
            }}
          >
            Login
          </button>

          <div className="SignIn-cont__signup">
            Don't have an account yet? <Link to={"/signup"}>Sign Up</Link>
          </div>
          <ThankYouSignUp
            image={SuccessImage}
            isOpened={isErroModalOpened}
            title={"Login Failed."}
            handleConfirm={closeErrorModal}
            EmailCheckText={getErrorFromBackend()}
            onClose={closeErrorModal}
          />
        </div>
      ) : (
        <div>Loading...</div>
      )}
      {!isMobile && <img src={SalyLeft} alt="" />}
    </section>
  );
};

export default SignIn;
